import { FormPasswordInput } from "Components/Shared/Inputs/Form/FormPasswordInput";
import { PasswordRequirements } from "Components/Shared/Inputs/PasswordRequirements";
import { SetPasswordFormErrorMessage } from "Components/Auth/SetPasswordPage/SetPasswordFormErrorMessage";
import {
  getSetPasswordValidationSchema,
  type SetPasswordFormModel,
} from "Components/Auth/SetPasswordPage/SetPasswordPageValidation";
import { Resources, useResource } from "Translations/Resources";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Button, Stack } from "@mui/material";
import { useForm } from "react-hook-form";
import { RequirementChecklist } from "Components/Shared/Inputs/RequirementChecklist";

const COMPONENT_RESOURCES = Resources.Pages.Content.SetPassword;

type Props = {
  isLoading: boolean;
  errorCode: string | null;
  onSubmit: (password: string) => void;
};

export const SetPasswordForm = ({ onSubmit, isLoading, errorCode }: Props) => {
  const { t } = useResource();
  const {
    handleSubmit,
    control,
    watch,
    formState: { isValid },
  } = useForm<SetPasswordFormModel>({
    resolver: zodResolver(getSetPasswordValidationSchema(t)),
    mode: "onChange",
    defaultValues: { password: "", passwordRepeated: "" },
  });

  const password = watch("password");
  const passwordRepeated = watch("passwordRepeated");

  const submit = ({ password }: SetPasswordFormModel) => {
    onSubmit(password);
  };

  return (
    <Stack
      mt={4}
      px={[2, 4]}
      component="form"
      display="flex"
      justifyContent="center"
      onSubmit={handleSubmit(submit)}
    >
      <Stack gap={1}>
        <FormPasswordInput
          control={control}
          name="password"
          label={t(COMPONENT_RESOURCES.Password)}
          isValidationHidden
        />
        <PasswordRequirements password={password ?? ""} />
      </Stack>
      <Stack gap={1} mt={4}>
        <FormPasswordInput
          control={control}
          name="passwordRepeated"
          label={t(COMPONENT_RESOURCES.PasswordConfirmation)}
          isValidationHidden
        />
        <RequirementChecklist
          items={[
            {
              id: "passwordsMatch",
              label: t(Resources.Validation.Password.MatchRequirement),
              isSatisfied:
                (passwordRepeated ?? "").length > 0 &&
                password === passwordRepeated,
            },
          ]}
        />
      </Stack>
      {errorCode && <SetPasswordFormErrorMessage errorCode={errorCode} />}
      <Box mt={4}>
        <Button
          fullWidth
          color="info"
          type="submit"
          size="large"
          variant="contained"
          disabled={!isValid}
          loading={isLoading}
          loadingPosition="start"
        >
          {t(COMPONENT_RESOURCES.SetPassword)}
        </Button>
      </Box>
    </Stack>
  );
};
